import React = require('react');
import { find, get, head, isEmpty, keys, map, reduce } from 'lodash';
import { Field } from 'redux-form';
import Loading from '../../../../../../components/Loading';
import { FormattedNumber } from 'react-intl';
import { windowSize } from '../../../../../../hooks/windowSize';
import { isRequired } from '../../../../../../util/validators';
import FieldFormControl from '../../../../../../components/FieldFormControl';
import { ButtonAsRadio } from '../ButtonRadioStyle';
import Benefit from '../../../../../Quote/scenes/SchemeChoice/components/Benefit';
import DefinitionPopup from '../../../../../../components/DefinitionPopup';
import { Button } from 'react-bootstrap';
import { useState } from 'react';

interface IMTASchemeChoiceProps {
  quotes: any
  product: any;
  tripType: string;
  schemeId: string;
  onSchemeChange: (schemeId: string) => void;
}

interface IBenefit {
  title: string;
  shortName: string;
  displayName: string;
  subBenefit?: string;
}

export const MTASchemeChoice = ({ quotes, product, tripType, schemeId, onSchemeChange }: IMTASchemeChoiceProps) => {

  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const isMobile = windowSize(480).isMobile;

  const BENEFITS: IBenefit[] = [
    { title: 'Medical expenses', shortName: 'Medical Expenses', displayName: 'Medical' },
    { title: 'Trip cancellation', shortName: 'Trip Cancellation', displayName: 'Cancellation' },
    { title: 'Personal belongings', shortName: 'Personal Belongings', displayName: 'Baggage' },
    {
      title: 'Overall valuables limit',
      shortName: 'Personal Belongings',
      displayName: 'Overall valuables limit',
      subBenefit: 'valuables total limit'
    },
    {
      title: 'Single item limit',
      shortName: 'Personal Belongings',
      displayName: 'Single item limit',
      subBenefit: 'single item limit'
    },
    { title: 'Money and documents', shortName: 'Money & Documents', displayName: null },
    { title: 'Excess per traveller', shortName: null, displayName: 'Policy Excess' },
  ]

  const ADDITIONAL_BENEFITS: IBenefit[] = [
    { title: 'Winter sports', shortName: 'Winter Sports', displayName: 'Winter sports' },
    { title: 'Cruise Cover', shortName: 'Cruise Cover', displayName: 'Cruise cover' },
    { title: 'Personal Accident', shortName: 'Personal Accident', displayName: 'Personal accident' },
    { title: 'Money & Documents', shortName: 'Money & Documents', displayName: 'Money & documents' },
    { title: 'Legal Liability', shortName: 'Legal Liability', displayName: 'Legal liability' },
    {
      title: 'Delayed/Missed Departure',
      shortName: 'Delayed/Missed Departure',
      displayName: 'Delayed/Missed departure'
    },
    { title: 'Legal Assistance', shortName: 'Legal Assistance', displayName: 'Legal assistance' },
    { title: 'Catastrophe', shortName: 'Catastrophe', displayName: 'Catastrophe' },
    { title: 'Hijack', shortName: 'Hijack', displayName: 'Hijack' },
    { title: 'Gadget cover', shortName: 'Gadget cover', displayName: 'Gadget cover' },
    { title: 'Excess Waiver', shortName: 'Excess Waiver', displayName: 'Excess waiver' },
  ]

  const isSelected = (quoteSchemeId: string) => {
    return quoteSchemeId === schemeId;
  }

  const onSchemeSelected = (selectedId: string) => () => {
    onSchemeChange(selectedId)
  }

  const findBenefit = (benefitToMatch: IBenefit, benefits: any[]) => {
    const benefit = find(benefits, ['short_name', benefitToMatch.shortName]);

    if (benefitToMatch?.subBenefit) return find(benefit.sub_benefits, ['name', benefitToMatch.subBenefit]);
    else return benefit;
  }

  const renderBenefitDescriptions = (benefitToMatch: IBenefit, quote) => {

    const benefits = Benefit.getBenefits(quote)
    const benefit = findBenefit(benefitToMatch, benefits);
    let description;

    switch (benefitToMatch.title) {
      case 'Excess per traveller':
        description = get(product, 'metadata.definitions.excess')
        break;
      default:
        description = get(benefit, 'description')
    }

    return (
      <div className={'benefit-title-container'}>
        <div className={'benefit-title'}>{benefitToMatch.title}</div>
        <div className={'benefit-definition'}>
          <DefinitionPopup
            name="benefit-info"
            definition={description}
            iconType="info"
          />
        </div>
      </div>
    )
  }

  const renderBenefitsSection = (quotes: any, benefitsData: IBenefit[]) => {
    const quoteForBenefits = head(quotes);

    return map(benefitsData, (benefit) => {
      return (
        <div className={'scheme-selection-container'}>
          <div
            className={'scheme-choice-header-title sub'}>{renderBenefitDescriptions(benefit, quoteForBenefits)}</div>
          {map(quotes, (quote, index) => {
            const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
            return (
              <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
                <Benefit
                  quote={quote}
                  shortName={benefit.shortName}
                  displayName={benefit.displayName}
                />
              </div>
            )
          })}
        </div>
      )
    })
  }

  const handleShowMoreInfo = () => {
    setShowMoreInfo(!showMoreInfo)
  }

  const renderMoreInfoSection = (quotes: any) => {
    const moreInfoButton = (
      <Button
        bsStyle="primary"
        type="button"
        className={'admiral-white-btn btn btn-default'}
        onClick={handleShowMoreInfo}
      >More Info
      </Button>
    )

    const image = require('../../Images/arrow-down-sign-to-navigate.svg')

    const moreInfoArrow = (
      <div onClick={handleShowMoreInfo}>
        <div>More Info</div>
        <img src={image} alt={''} className={'down-arrow'}/>
      </div>
    )

    return (
      <div className={'scheme-selection-container'}>
        <div className={'scheme-choice-header-title sub'}>View more policy features</div>
        {map(quotes, (quote, index) => {
          const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
          return (
            <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
              {!isMobile && moreInfoButton}
              {isMobile && moreInfoArrow}
            </div>
          )
        })}
      </div>
    )
  }

  const renderSchemeSelection = (quotes) => {
    const disabled = false;
    let hasDiscount = false;

    return (
      <div className={'scheme-selection-container'}>
        <div className={'scheme-choice-header-title'}/>
        {map(quotes, (quote, index) => {
          const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
          hasDiscount = get(quote, 'discount') < 0;
          const discountedPrice = get(quote, 'premium_before_adjustments.gross') ? get(quote, 'premium_before_adjustments.gross').toFixed(2) : 0.00;
          const schemeId = get(quote, 'scheme.id');
          return (
            <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
              <div className={'scheme-price-container'}>
                {hasDiscount && (
                  <div className={'discount-price-strikethrough'}>
                    {'£' + discountedPrice}
                  </div>
                )}
                <FormattedNumber
                  value={quote?.premium?.gross}
                  style="currency"
                  currency={'GBP'}
                />
              </div>

              {!isMobile && (
                <Field
                  name="schemeId"
                  simpleValue={schemeId}
                  className={'admiral-white-btn'}
                  style={{ minWidth: '90px' }}
                  selectedText={'SELECTED'}
                  unSelectedText={'SELECT'}
                  disabled={disabled}
                  component={ButtonAsRadio}
                  validate={[isRequired]}
                  handleChange={onSchemeSelected(schemeId)}
                />
              )}

              {isMobile && (
                <div className={'scheme-choice-mobile-radio'}>
                  <Field
                    name="schemeId"
                    type="radio"
                    value={schemeId}
                    complexValue={true}
                    component={FieldFormControl}
                    validate={[isRequired]}
                    disabled={disabled}
                    onChange={onSchemeSelected(schemeId)}
                  >
                    <div/>
                  </Field>
                </div>
              )}

            </div>
          )
        })}
      </div>
    );
  }

  const renderQuotes = () => {
    if (isEmpty(quotes) || !product) {
      return (
        <Loading/>
      )
    }

    if (!tripType) {
      return null;
    }

    const documentsOrder = [
      'Insurance Product Information Document',
      'Policy Wording',
    ];

    return (
      <>
        <div style={{ maxWidth: '650px', margin: 'auto' }}>
          <div className={'scheme-selection-container'}>
            <div className={'scheme-choice-header-title'}>&nbsp;</div>
            {map(quotes, (quote, index) => {
              const image = require('../../Images/' + get(quote, 'scheme.cover_level') + '-level.svg')
              const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
              return (
                <div className={`scheme-choice-cover-levels ${isSchemeSelected}`}>
                  <img src={image} alt="Admiral" className={'cover-level-image'}/>
                </div>
              )
            })}
          </div>

          {renderSchemeSelection(quotes)}
          {renderBenefitsSection(quotes, BENEFITS)}
          {renderMoreInfoSection(quotes)}
          {showMoreInfo && renderBenefitsSection(quotes, ADDITIONAL_BENEFITS)}

          <div className={'scheme-selection-container'}>
            <div className={'scheme-choice-header-title sub'}>Insurance Product Information Document</div>
            {map(quotes, (quote, index) => {
              const isSchemeSelected = isSelected(get(quote, 'scheme.id')) ? 'selected' : '';
              const documents = get(quote, 'scheme.documents');
              const isAnnual = tripType === 'Annual Multi Trip';
              const coverLevel = get(quote, 'scheme.cover_level');

              const filteredDocuments = reduce(keys(documents), (acc: any, current: string) => {
                const isIpid = current.toLowerCase().includes('ipid');
                const isCoverLevel = current.toLowerCase().includes(coverLevel);
                const isSingle = current.toLowerCase().includes(tripType.toLowerCase());
                const isMultiTrip = isAnnual && current.toLowerCase().includes('multi-trip');

                if (!isIpid || (isCoverLevel && (isSingle || isMultiTrip))) {
                  acc[current] = documents[current];
                }

                return acc;
              }, {});

              if (quote === null) {
                return null;
              }
              return (
                <div className={`scheme-choice-cover-levels ${isSchemeSelected}`} key={index}>
                  {map(documentsOrder, (documentName: string) => {
                    const documentUrl = get(filteredDocuments, documentName);
                    if (!documentUrl || documentName !== 'Insurance Product Information Document') {
                      return null;
                    }

                    return (
                      <div>
                        <a className={'scheme-document-link'} href={documentUrl} target="_blank"
                           key={documentName}>
                          <img src={require('../../Images/document.svg')} alt={'Admiarl'}/>
                          <div>OPEN</div>
                        </a>
                      </div>
                    );
                  })}
                </div>
              )
            })}
          </div>

          {renderSchemeSelection(quotes)}
        </div>
      </>
    )
  }

  return (
    <>
      {renderQuotes()}
    </>
  )
}
